import { useCallback, useSyncExternalStore } from 'react';
import type { OperationDefinition } from '@/types/operations';
import {
  getFavoriteConnectors,
  getFavoriteOperations,
  isFavoriteConnector,
  isFavoriteOperation,
  subscribeFavoriteOperations,
  subscribeFavorites,
  toggleFavoriteConnector,
  toggleFavoriteOperation,
  normalizeFavoriteId,
} from '@/services/favoritesService';

/**
 * React hook for reading and mutating the user's favorite connectors.
 *
 * Subscribes to the favorites store so any change (in this tab or another)
 * triggers a re-render. The returned `favorites` array is the canonical
 * normalized list; use `isFavorite(id)` for normalized membership checks.
 */
export const useFavorites = () => {
  const favorites = useSyncExternalStore(
    subscribeFavorites,
    getFavoriteConnectors,
    getFavoriteConnectors,
  );

  const isFavorite = useCallback(
    (connectorId: string) => isFavoriteConnector(connectorId),
    // `favorites` is included so memoized consumers re-evaluate after a change.
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [favorites],
  );

  const toggle = useCallback((connectorId: string) => {
    toggleFavoriteConnector(connectorId);
  }, []);

  return {
    favorites,
    isFavorite,
    toggle,
    normalize: normalizeFavoriteId,
  };
};

/**
 * React hook for reading and mutating the user's favorite operations.
 *
 * Operations are stored as full `OperationDefinition` snapshots so the picker
 * can render and add them without re-fetching from the backend.
 */
export const useFavoriteOperations = () => {
  const favoriteOperations = useSyncExternalStore(
    subscribeFavoriteOperations,
    getFavoriteOperations,
    getFavoriteOperations,
  );

  const isFavoriteOp = useCallback(
    (operationId: string) => isFavoriteOperation(operationId),
    // `favoriteOperations` keeps memoized consumers in sync after changes.
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [favoriteOperations],
  );

  const toggleOp = useCallback((operation: OperationDefinition) => {
    toggleFavoriteOperation(operation);
  }, []);

  return {
    favoriteOperations,
    isFavoriteOperation: isFavoriteOp,
    toggleFavoriteOperation: toggleOp,
  };
};

